import Service, { inject as service } from '@ember/service';
import { isBlank } from '@ember/utils';
import { task } from 'ember-concurrency';

import APINotFoundError from 'later/errors/api-not-found-error';
import { RequestError } from 'later/errors/fetch';
import InvalidCredentialsError from 'later/errors/invalid-credentials-error';
import { INSTAGRAM_GRAPH_URL } from 'later/utils/constants';
import { fetch } from 'later/utils/fetch';
import createIgPostFromBasicDisplay from 'later/utils/formatters/ig-post-from-basic-display';
import createIgUserFromBasicDisplay from 'later/utils/formatters/ig-user-from-basic-display';

import type IntlService from 'ember-intl/services/intl';
import type SocialProfileModel from 'later/models/social-profile';
import type AlertsService from 'later/services/alerts';
import type { InstagramMedia, InstagramRequestError, RecentInstagramMediaBasicDisplay } from 'shared/types/instagram';

export default class InstagramBasicDisplayService extends Service {
  @service declare alerts: AlertsService;
  @service declare intl: IntlService;

  baseURL = INSTAGRAM_GRAPH_URL;

  fetchSelf = task(async (socialProfile: SocialProfileModel) => {
    const fields = ['id', 'username', 'media_count', 'account_type'].join(',');
    const query = `fields=${fields}&access_token=${socialProfile.basicDisplayToken}`;

    try {
      const response = await fetch(`${this.baseURL}/me?${query}`);
      return createIgUserFromBasicDisplay(response, socialProfile);
    } catch (error) {
      return undefined;
    }
  });

  fetchUsername = task(async (token: string) => {
    const query = `fields=username&access_token=${token}`;
    try {
      const { username } = await fetch(`${this.baseURL}/me?${query}`);
      return username;
    } catch (error) {
      return undefined;
    }
  });

  fetchMedia = task(async (socialProfile: SocialProfileModel, postId: string) => {
    const fields = [
      'id',
      'username',
      'caption',
      'media_type',
      'media_url',
      'permalink',
      'thumbnail_url',
      'timestamp'
    ].join(',');
    const query = `fields=${fields}&access_token=${socialProfile.basicDisplayToken}`;
    try {
      const response = await fetch(`${this.baseURL}/${postId}?${query}`);
      return createIgPostFromBasicDisplay(response, socialProfile);
    } catch (error) {
      if (error.code === 404) {
        throw new APINotFoundError({ title: 'APINotFoundError', detail: error.message, code: error.code });
      }
      return undefined;
    }
  });

  fetchRecentMedia = task(
    async (
      socialProfile: SocialProfileModel,
      params?: { next?: string }
    ): Promise<RecentInstagramMediaBasicDisplay> => {
      const fields = [
        'id',
        'username',
        'caption',
        'media_type',
        'media_url',
        'permalink',
        'thumbnail_url',
        'timestamp'
      ].join(',');

      const token = socialProfile.basicDisplayToken;

      if (!token) {
        throw new InvalidCredentialsError(this.intl.t('errors.missing_basic_display_token'));
      }

      const builtUrl = `${this.baseURL}/${socialProfile.basicDisplayId}/media?fields=${fields}&access_token=${token}`;
      const query = params && params.next ? params.next : builtUrl;
      try {
        const { data, paging } = await fetch(query);
        return {
          posts: await Promise.all(
            data.map((post: InstagramMedia) => createIgPostFromBasicDisplay(post, socialProfile))
          ),
          pagination: paging
        };
      } catch (error) {
        let graphErrorCode: number | undefined;
        if (error instanceof RequestError && error.responseCode !== undefined) {
          graphErrorCode = Number(error.responseCode);
        } else if (typeof (error as { code?: unknown }).code === 'number') {
          graphErrorCode = (error as { code: number }).code;
        }

        if (graphErrorCode === 190) {
          throw new InvalidCredentialsError(error.message);
        }
        throw error;
      }
    }
  );

  /**
   * Helper for displaying request errors.
   */
  raiseError(error: InstagramRequestError): void {
    if (error) {
      if (error.errors) {
        this.alerts.warning(error.errors[0].detail);
      } else if (!isBlank(error.message)) {
        this.alerts.warning(error.message);
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'instagram-basic-display': InstagramBasicDisplayService;
  }
}
